import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ApiError } from '../lib/api';
import { loadRazorpay } from '../lib/razorpay';
import { TypeTheme } from '../lib/types';
import { grad } from '../components/ui';

export function Cart() {
  const navigate = useNavigate();
  const { cartProducts, cartTotal, removeFromCart, checkout, confirmPayment } = useApp();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const savings = cartTotal.mrp - cartTotal.price;

  async function placeOrder() {
    if (busy) return;
    setBusy(true);
    try {
      const result = await checkout();
      if (result.status === 'done') {
        setDone(true);
        setBusy(false);
        return;
      }
      // Razorpay checkout
      const ok = await loadRazorpay();
      if (!ok) throw new ApiError('Could not load payment gateway.', 0);
      const rzp = new (window as any).Razorpay({
        key: result.keyId,
        order_id: result.orderId,
        amount: result.amount,
        currency: result.currency,
        name: 'PulsePrep',
        description: 'Purchase',
        theme: { color: '#5A2D9C' },
        handler: async (resp: any) => {
          try {
            await confirmPayment({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setDone(true);
          } catch (e) {
            alert(e instanceof ApiError ? e.message : 'Payment verification failed.');
          } finally {
            setBusy(false);
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.open();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Could not place the order.');
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="col center" style={{ padding: '60px 0', gap: 10, textAlign: 'center' }}>
        <div className="center" style={{ width: 90, height: 90, borderRadius: '50%', background: 'var(--success)', color: '#fff', fontSize: 44 }}>✓</div>
        <h1 style={{ fontSize: 24, fontWeight: 900, marginTop: 10 }}>Payment successful! 🎉</h1>
        <p className="muted" style={{ maxWidth: 340 }}>Your packs are now in your library with lifetime access.</p>
        <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => navigate('/library')}>Go to My Library</button>
        <button className="link" style={{ background: 'none', border: 'none', marginTop: 6 }} onClick={() => navigate('/store')}>Continue shopping</button>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 20 }}>
      <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 16 }}>My Cart</h1>

      {cartProducts.length === 0 ? (
        <div className="col center" style={{ padding: 50, gap: 8 }}>
          <div style={{ fontSize: 48 }}>🛒</div>
          <div style={{ fontWeight: 800 }}>Your cart is empty</div>
          <button className="btn btn-primary" style={{ marginTop: 8 }} onClick={() => navigate('/store')}>Browse store</button>
        </div>
      ) : (
        <div className="grid grid-2" style={{ alignItems: 'start' }}>
          <div>
            {cartProducts.map((p) => {
              const t = TypeTheme[p.type];
              return (
                <div key={p.id} className="card row gap12" style={{ marginBottom: 12 }}>
                  <div className="thumb" style={{ ...grad(p.gradient), width: 56, height: 56, fontSize: 26 }}>{p.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <span className="badge tc-badge" style={{ ['--c' as string]: t.color } as React.CSSProperties}>{t.label}</span>
                    <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>{p.title}</div>
                    <div className="price" style={{ marginTop: 4 }}>{p.price === 0 ? 'FREE' : `₹${p.price}`}</div>
                  </div>
                  <button className="icon-btn" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }} onClick={() => removeFromCart(p.id)} aria-label="Remove">🗑️</button>
                </div>
              );
            })}
          </div>

          <div className="card">
            <h3 style={{ fontWeight: 800, marginBottom: 12 }}>Bill details</h3>
            <Row label="Item total" value={`₹${cartTotal.mrp}`} />
            {savings > 0 && <Row label="Discount" value={`- ₹${savings}`} good />}
            <Row label="Platform fee" value="₹0" />
            <div style={{ height: 1, background: 'var(--divider)', margin: '10px 0' }} />
            <Row label="To pay" value={`₹${cartTotal.price}`} bold />
            {savings > 0 && (
              <div style={{ background: 'var(--success-soft)', color: 'var(--success)', textAlign: 'center', borderRadius: 8, padding: 8, marginTop: 12, fontSize: 13, fontWeight: 700 }}>
                🎉 You&apos;re saving ₹{savings} on this order
              </div>
            )}
            <button className="btn btn-primary btn-block" style={{ marginTop: 16 }} onClick={placeOrder} disabled={busy}>
              {busy ? 'Processing…' : `🔒 Place order · ₹${cartTotal.price}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, bold, good }: { label: string; value: string; bold?: boolean; good?: boolean }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', margin: '6px 0' }}>
      <span className={bold ? '' : 'muted'} style={{ fontWeight: bold ? 800 : 400, fontSize: bold ? 16 : 14 }}>{label}</span>
      <span style={{ fontWeight: bold ? 900 : 700, fontSize: bold ? 18 : 14, color: good ? 'var(--success)' : 'var(--text)' }}>{value}</span>
    </div>
  );
}
